import React, { createContext, useEffect, useState } from "react";
import { nanoid } from "@reduxjs/toolkit";
import { PythonExecutable } from "../executable";
import { useUniqueProjectID } from "../hooks";

export enum TaskStatus {
  Waiting,
  Running,
  Finished
}

interface ExecutableTask {
  id: string,
  commands: string[],
  status: TaskStatus,
  exe: PythonExecutable,
  onFinish: Function
}

export const TaskQueueContext = createContext({
  taskQueue: [] as ExecutableTask[],
  addRunningTask: (commands: string[], onFinish: Function) => {},
  stopRunningTask: (task: string) => {},
  clearTaskQueue: () => {}
});

export const TaskQueueProvider = (props) => {
  const [taskQueue, setTaskQueue] = useState<ExecutableTask[]>([]);
  const [running, setRunning] = useState(false);
  const uniqueProjectID = useUniqueProjectID();

  const runningTask = taskQueue.find(x => x.status === TaskStatus.Running);

  const addRunningTask = (commands: string[], onFinish: Function) => {
    const newTask = {
      id: nanoid(),
      onFinish,
      commands,
      exe: new PythonExecutable(),
      status: TaskStatus.Waiting
    } as ExecutableTask;

    setTaskQueue([...taskQueue, newTask]);
  };

  const clearTaskQueue = () => {
    runningTask?.exe.destroy();
    setTaskQueue([]);
    setRunning(false);
  };

  const stopRunningTask = (taskID: string) => {
    const task = taskQueue.find(x => x.id === taskID);
    if (!task) return;

    task.exe.destroy();
    setRunning(false);
  };

  const updateTaskStatus = (taskID, status) => {
    const tasks = taskQueue.map(task => {
      if (task.id === taskID) {
        return {...task, status: status};
      }

      return task;
    });

    setTaskQueue([...tasks]);
  };

  const runAvailableTask = () => {
    const availableTasks = taskQueue
      .filter(x => x.status === TaskStatus.Waiting);

    if ((availableTasks.length === 0) || runningTask) return;

    const task = availableTasks[0];
    setRunning(true);
    updateTaskStatus(task.id, TaskStatus.Running);

    task.exe.run({
      command: task.commands,
      saveLogs: true,
      onClose: async (error) => {
        if (error) return;
        setRunning(false);
        await task.onFinish();
      }
    });
  };

  useEffect(() => {
    /** Clear task queue when new project is loaded */
    clearTaskQueue();
  }, [uniqueProjectID]);

  useEffect(() => {
    runAvailableTask();
  }, [taskQueue]);

  useEffect(() => {
    if (running || !runningTask) return;
    updateTaskStatus(runningTask.id, TaskStatus.Finished);
  }, [running]);

  return (
    <TaskQueueContext.Provider value={{
      taskQueue,
      addRunningTask,
      stopRunningTask,
      clearTaskQueue
    }}>
      {props.children}
    </TaskQueueContext.Provider>
  );
};
